import React, { useState } from 'react';
import { BiPencil } from 'react-icons/bi';
import { BsArrowLeftRight, BsCalendar3, BsCheckCircle, BsEnvelope, BsLink45Deg, BsPersonStanding, BsPersonStandingDress, BsPersonWorkspace, BsShieldLock } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import { ProcessoUtils, StatusProcesso } from '../../../model/processo/Processo';
import { SalaViewModel } from '../../../viewmodel/ProcessoSalaViewModel';
import { ProcessoViewModel } from '../../../viewmodel/ProcessoViewModel';
import AcoesAdminModal from './AcoesAdminModal';
import AssociarContaServicoModal from './AssociarContaServicoModal';
import CandidatosModal from './CandidatosModal';
import CategoriaIcone from './CategoriaIcone';
import EditaisProcessoModal from './EditaisProcessoModal';
import EditarProcessoModal from './EditarProcessoModal';
import FinalizarProcessoModal from './FinalizarProcessoModal';
import IntegracaoCandidatosModal from './IntegracaoCandidatosModal';
import IntegracaoSigaModal from './IntegracaoSigaModal';
import SalaEsperaModal from './SalaEsperaModal';
import StatusModal from './StatusModal';
import StatusCandidatosModal from './StatusCandidatosModal';
import './TabelaProcessos.css';
import PermissaoWrapper from '../../../components/PermissaoWrapper';
import { PermissaoEnum } from '../../../model/Usuario/Permissao';

interface TabelaProcessosProps {
  processos: any[];
  loading: boolean;
  unidades: any[];
  onProcessoUpdated: () => void;
  visualizacaoDetalhada: boolean;
}

const TabelaProcessos: React.FC<TabelaProcessosProps> = ({ processos, loading, unidades, onProcessoUpdated, visualizacaoDetalhada }) => {
  const navigate = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<StatusProcesso | null>(null);
  const [showCandidatosModal, setShowCandidatosModal] = useState(false);
  const [showSalaEsperaModal, setShowSalaEsperaModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [selectedProcesso, setSelectedProcesso] = useState<any>(null);
  const [salaEsperaSalva, setSalaEsperaSalva] = useState<Set<string>>(new Set());
  const [showEditaisModal, setShowEditaisModal] = useState(false);
  const [modalEditais, setModalEditais] = useState<any[]>([]);
  const [selectedProcessoNome, setSelectedProcessoNome] = useState<string>('');
  const [showAssociarContaModal, setShowAssociarContaModal] = useState(false);
  const [showIntegracaoSigaModal, setShowIntegracaoSigaModal] = useState(false);
  const [showIntegracaoCandidatosModal, setShowIntegracaoCandidatosModal] = useState(false);
  const [showAcoesAdminModal, setShowAcoesAdminModal] = useState(false);
  const [showStatusCandidatosModal, setShowStatusCandidatosModal] = useState(false);

  const handleStatusClick = (status: StatusProcesso) => {
    setSelectedStatus(status);
    setShowStatusModal(true);
  };

  const handleCandidatosClick = (processo: any) => {
    setSelectedProcesso(processo);
    setShowCandidatosModal(true);
  };

  const handleEditarProcesso = (processo: any) => {
    setSelectedProcesso(processo);
    setShowEditarModal(true);
  };

  const handleCronograma = (processo: any) => {
    navigate(`/processos/${processo.id}/cronograma`);
  };

  const handleBancas = (processo: any) => {
    navigate(`/processos/${processo.id}/bancas`);
  };

  const handleSalaEspera = (processo: any) => {
    setSelectedProcesso(processo);
    setShowSalaEsperaModal(true);
  };

  const handleSaveSalaEspera = async (processoId: string, salaUrl: string) => {
    try {
      const processo = selectedProcesso;
      if (processo?.salaEspera?.id) {
        await SalaViewModel.patchLinkSala(processo.salaEspera.id, salaUrl);
      } else {
        await SalaViewModel.criarSala({ id: '', salaUrl }, processoId);
      }
      setSalaEsperaSalva(prev => new Set(prev).add(processoId));
    } catch (error) {
      throw error;
    }
  };

  const handleSaveProcesso = async (processoAtualizado: any) => {
    await ProcessoViewModel.atualizarProcesso(processoAtualizado.id, processoAtualizado);
    onProcessoUpdated();
  };

  const handleCancelProcesso = async (processoId: string) => {
    await ProcessoViewModel.cancelarProcesso(processoId);
    onProcessoUpdated();
  };

  const handleFinalizarProcesso = (processo: any) => {
    setSelectedProcesso(processo);
    setShowFinalizarModal(true);
  };

  const handleConfirmFinalizar = async (processoId: string) => {
    try {
      await ProcessoViewModel.finalizarProcesso(processoId);
      toast.success('Processo finalizado com sucesso!');
      onProcessoUpdated();
    } catch (error) {
      toast.error(BackendErrorMessage(error));
    }
  };

  const handleIntegracaoSiga = (processo: any) => {
    setSelectedProcesso(processo);
    setShowIntegracaoSigaModal(true);
  };

  const handleIntegracaoCandidatos = (processo: any) => {
    setSelectedProcesso(processo);
    setShowIntegracaoCandidatosModal(true);
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <div className="table-container">
        <div className="loading-container">
          <div className="spinner-border" role="status"></div>
        </div>
      </div>
    );
  }

  if (processos.length === 0) {
    return (
      <div className="table-container">
        <div className="empty-state">
          <p>Nenhum processo encontrado com os filtros aplicados.</p>
        </div>
      </div>
    );
  }

  const algumProcessoFinalizavel = processos.some(p =>
    p.finalizavel
  );

  return (
    <div className="table-container">
      <div className="table-responsive">
        <table className="processos-table">
          <thead>
            <tr>
              <th>Editais</th>
              <th>Identificador</th>
              {visualizacaoDetalhada && <th>Responsável</th>}
              <th>Unidade</th>
              <th>Status</th>
              {visualizacaoDetalhada && <th>Data Criação</th>}
              <th style={{ textAlign: 'center' }}>Candidatos</th>
              <th style={{ textAlign: 'center' }}>Ações</th>
              <th style={{ textAlign: 'center' }}>Bancas</th>
              {algumProcessoFinalizavel && <th style={{ textAlign: 'center' }}>Finalizar</th>}
            </tr>
          </thead>
          <tbody>
            {processos.map((processo: any) => (
              <tr 
                key={processo.id}
                className={processo.hasIntegracaoSiga ? 'processo-integracao-siga' : ''}
              >
                <td>
                  {processo.editais && processo.editais.length > 0 ? (
                    processo.editais.length === 1 ? (
                      <span>
                        <CategoriaIcone
                          categoria={processo.editais[0].categoria}
                          size={18}
                        />
                        {processo.editais[0].nome}</span>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CategoriaIcone
                          categoria={processo.editais[0].categoria}
                          size={18}
                        />
                        <button
                          className="btn"
                          style={{
                            backgroundColor: '#f8f9fa',
                            color: 'black',
                            border: '1px solid gray',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                          onClick={() => {
                            setModalEditais(processo.editais);
                            setSelectedProcessoNome(processo.nome);
                            setShowEditaisModal(true);
                          }}
                        >
                          ({processo.editais.length})
                        </button>
                      </div>
                    )
                  ) : (
                    <span>-</span>
                  )}
                </td>

                <td>
                  {processo.contasServico && processo.contasServico.length > 0 ? (
                    <span title={`Contas de Serviço: ${processo.contasServico.map((c: any) => `${c.descricao} (${c.unidade.sigla})`).join(', ')}`} style={{ cursor: 'help' }}>
                      {processo.identificador}
                    </span>
                  ) : (
                    processo.identificador
                  )}
                </td>
                {visualizacaoDetalhada && <td>{processo.responsavel}</td>}
                <td>{processo.unidade?.nome || '-'}</td>
                <td>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: ProcessoUtils.getStatusColor(processo.status),
                      color: '#000',
                      cursor: 'pointer',
                      padding: '6px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                    }}
                    onClick={() => handleStatusClick(processo.status)}
                  >
                    {processo.status}
                  </span>
                </td>
                {visualizacaoDetalhada && <td>{formatDate(processo.dataCriacao)}</td>}
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <button
                      style={{
                        backgroundColor: '#f8f9fa',
                        color: 'black',
                        border: '1px solid gray',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        position: 'relative',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      title="Candidatos"
                      onClick={() => handleCandidatosClick(processo)}
                    >
                      <BsPersonStandingDress style={{ marginRight: '-2px' }} />
                      <BsPersonStanding />
                      <span style={{ marginLeft: '4px' }}>{processo.numeroCandidatos || 0}</span>
                      {processo.convocacoesEnviadas && (
                        <BsEnvelope style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          color: '#007bff',
                          fontSize: '10px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          padding: '1px'
                        }} />
                      )}
                    </button>
                    <PermissaoWrapper
                      permissoesRequiridas={[
                        PermissaoEnum.ABRIR_INTEGRACAO_PROCESSO_CANDIDATOS,
                        PermissaoEnum.FINALIZAR_INTEGRACAO_PROCESSO_CANDIDATOS
                      ]}
                      verificarQualquerPermissao={true}
                    >
                      <button
                        className={processo.integracaoCandidatosAberta ? 'integracao-candidatos-aberta' : ''}
                        style={{
                          backgroundColor: processo.integracaoCandidatosAberta ? '#d1fae5' : '#f5f5f5',
                          color: 'black',
                          border: `1px solid ${processo.integracaoCandidatosAberta ? '#22c55e' : 'gray'}`,
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          position: 'relative',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                        title={processo.integracaoCandidatosAberta ? 'Fechar integração de candidatos' : 'Abrir integração de candidatos'}
                        onClick={() => handleIntegracaoCandidatos(processo)}
                      >
                        <BsArrowLeftRight />
                      </button>
                    </PermissaoWrapper>
                  </div>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <button
                      style={{
                        backgroundColor: processo.nomeEditavel ? '#e3f2fd' : '#f5f5f5',
                        color: 'black',
                        border: '1px solid gray',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: processo.nomeEditavel ? 'pointer' : 'not-allowed',
                        opacity: processo.nomeEditavel ? 1 : 0.6,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      title="Editar Processo"
                      onClick={() => handleEditarProcesso(processo)}
                      //TODO: Descomentar o disabled original
                      //disabled={!processo.nomeEditavel}
                    >
                      <BiPencil />
                    </button>
                    <button
                      style={{
                        backgroundColor: '#f5f5f5',
                        color: 'black',
                        border: '1px solid gray',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        position: 'relative',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      title="Sala de Espera"
                      onClick={() => handleSalaEspera(processo)}
                    >
                      <BsLink45Deg />
                      {(processo.salaEspera?.salaUrl || salaEsperaSalva.has(processo.id)) && (
                        <span style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          color: 'green',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>✓</span>
                      )}
                    </button>
                    <button
                      style={{
                        backgroundColor: '#fff3cd',
                        color: 'black',
                        border: '1px solid gray',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        position: 'relative',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      title="Cronograma"
                      onClick={() => handleCronograma(processo)}
                    >
                      <BsCalendar3 />
                      {processo.cronogramaDefinido && (
                        <span style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          color: 'green',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>✓</span>
                      )}
                    </button>
                    <PermissaoWrapper permissoesRequiridas={[PermissaoEnum.ADMINISTRADOR]}>
                      <button
                        style={{
                          backgroundColor: '#e8daef',
                          color: 'black',
                          border: '1px solid gray',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                        }}
                        title="Ações Administrativas"
                        onClick={() => {
                          setSelectedProcesso(processo);
                          setShowAcoesAdminModal(true);
                        }}
                      >
                        <BsShieldLock />
                      </button>
                    </PermissaoWrapper>
                  </div>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <button
                      style={{
                        backgroundColor: '#f0e68c',
                        color: 'black',
                        border: '1px solid gray',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        position: 'relative',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                      }}
                      title="Bancas"
                      onClick={() => handleBancas(processo)}
                    >
                      <BsPersonWorkspace />
                      {processo.bancasTerminadas && (
                        <span style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          color: 'green',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>✓</span>
                      )}
                    </button>
                  </div>
                </td>
                {algumProcessoFinalizavel && (
                  <td style={{ textAlign: 'center' }}>
                    {processo.finalizavel ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <button
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                          title="Finalizar Processo"
                          onClick={() => handleFinalizarProcesso(processo)}
                        >
                          <BsCheckCircle size={16} />
                        </button>
                      </div>
                    ) : (
                      <span></span> // Ou deixar vazio: <></>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showStatusModal && selectedStatus && (
        <StatusModal
          selectedStatus={selectedStatus}
          onClose={() => setShowStatusModal(false)}
        />
      )}

      {showCandidatosModal && selectedProcesso && (
        <CandidatosModal
          show={showCandidatosModal}
          processo={selectedProcesso}
          onClose={() => setShowCandidatosModal(false)}
        />
      )}

      {showSalaEsperaModal && selectedProcesso && (
        <SalaEsperaModal
          show={showSalaEsperaModal}
          processo={selectedProcesso}
          onClose={() => setShowSalaEsperaModal(false)}
          onSave={handleSaveSalaEspera}
          onProcessoUpdated={onProcessoUpdated}
        />
      )}

      {showEditarModal && selectedProcesso && (
        <EditarProcessoModal
          show={showEditarModal}
          processo={selectedProcesso}
          unidades={unidades}
          onClose={() => setShowEditarModal(false)}
          onSave={handleSaveProcesso}
          onCancel={handleCancelProcesso}
        />
      )}

      {showFinalizarModal && selectedProcesso && (
        <FinalizarProcessoModal
          show={showFinalizarModal}
          processo={selectedProcesso}
          onClose={() => setShowFinalizarModal(false)}
          onConfirm={handleConfirmFinalizar}
        />
      )}

      {showEditaisModal && (
        <EditaisProcessoModal
          show={showEditaisModal}
          onHide={() => setShowEditaisModal(false)}
          editais={modalEditais}
          processoNome={selectedProcessoNome}
        />
      )}

      {showAssociarContaModal && selectedProcesso && (
        <AssociarContaServicoModal
          show={showAssociarContaModal}
          processo={selectedProcesso}
          onClose={() => setShowAssociarContaModal(false)}
          onSuccess={onProcessoUpdated}
        />
      )}

      {showIntegracaoSigaModal && selectedProcesso && (
        <IntegracaoSigaModal
          show={showIntegracaoSigaModal}
          processoId={selectedProcesso.id}
          processoNome={selectedProcesso.nome}
          onClose={() => setShowIntegracaoSigaModal(false)}
          onSuccess={onProcessoUpdated}
        />
      )}

      {showIntegracaoCandidatosModal && selectedProcesso && (
        <IntegracaoCandidatosModal
          show={showIntegracaoCandidatosModal}
          processoId={selectedProcesso.id}
          processoNome={selectedProcesso.nome}
          onClose={() => setShowIntegracaoCandidatosModal(false)}
          onSuccess={onProcessoUpdated}
        />
      )}

      {showAcoesAdminModal && selectedProcesso && (
        <AcoesAdminModal
          show={showAcoesAdminModal}
          processo={selectedProcesso}
          onClose={() => setShowAcoesAdminModal(false)}
          onAssociarConta={() => setShowAssociarContaModal(true)}
          onIntegracaoSiga={() => setShowIntegracaoSigaModal(true)}
          onStatusCandidatos={() => setShowStatusCandidatosModal(true)}
        />
      )}

      {showStatusCandidatosModal && selectedProcesso && (
        <StatusCandidatosModal
          show={showStatusCandidatosModal}
          processo={selectedProcesso}
          onClose={() => setShowStatusCandidatosModal(false)}
        />
      )}
    </div>
  );
};

export default TabelaProcessos;
